import { InputLabel, OutlinedInput, Stack, Typography } from '@mui/material';
import { Form, useNavigation } from '@remix-run/react';

import { TechnicalWarning } from '@tbd/life-jsx/SystemIcons';
import { PasswordInput } from '@tbd/lift-ui/components/PasswordInput';
import { TextButton } from '@tbd/lift-ui/components/TextButton';

import { LiftPortalAlert } from '#app/components/LiftPortalAlert.js';
export interface SignInProps {
  action: string;
  redirectTo?: string;
  errors?: Record<string, string>;
  forgotPassword?: boolean;
}

const EMPTY_ERRORS: Record<string, string> = {};
export function SignIn(props: SignInProps) {
  const { action, errors = EMPTY_ERRORS, redirectTo } = props;

  const navigation = useNavigation();
  const usernameError = errors?.username;
  const hasUsernameError = Boolean(usernameError);
  const passwordError = errors?.password;
  const hasPasswordError = Boolean(passwordError);

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Stack>
        <Typography variant="header-xs">Log in to LIFT Portal</Typography>
        <Typography variant="component-regular-m">
          Access all services with your user account.
        </Typography>
      </Stack>
      {'login' in errors ? (
        <LiftPortalAlert
          severity="error"
          icon={<TechnicalWarning />}
          message={errors.login}
          sx={{
            padding: '0px 12px',
          }}
        />
      ) : null}
      <Form replace method="post" action={action}>
        <Stack
          direction="column"
          sx={{
            gap: 2,
          }}
        >
          <input type="hidden" name="redirectTo" value={redirectTo} />
          <Stack>
            <InputLabel error={hasUsernameError} htmlFor="username">
              <Typography variant="component-regular-xxs">
                Username or email address
              </Typography>
            </InputLabel>
            <OutlinedInput
              required
              autoComplete="username"
              autoFocus
              error={hasUsernameError}
              fullWidth
              id="username"
              name="username"
              placeholder="e.g. annie@laerdal.com"
              size="small"
              sx={{ p: 1, backgroundColor: 'white' }}
              type="text"
              inputProps={{
                title:
                  'Either A) a valid email OR B) a username between 3-64 characters (no spaces)',
                minLength: 3,
                pattern:
                  "^[a-zA-Z0-9]{3,64}|[a-zA-Z0-9\\.\\{\\}\\-\\|`_^?=\\/+*'&%$#!~]+@[a-zA-Z0-9\\.\\-]+\\.[a-zA-Z]{2,}$",
              }}
            />
            {usernameError ? (
              <LiftPortalAlert severity="error" message={usernameError} />
            ) : null}
          </Stack>

          <Stack>
            <InputLabel error={hasPasswordError} htmlFor="password">
              <Typography variant="component-regular-xxs">Password</Typography>
            </InputLabel>
            <PasswordInput
              autoComplete="current-password"
              placeholder="Enter your password"
              error={hasPasswordError}
              sx={{ backgroundColor: 'white' }}
            />
            {passwordError ? (
              <LiftPortalAlert severity="error" message={passwordError} />
            ) : null}
          </Stack>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <TextButton
              size="small"
              href="/forgot-password"
              variant="tertiary"
              text="Forgot password?"
            />
          </Stack>
          <Stack>
            <TextButton
              type="submit"
              size="medium"
              variant="primary"
              disabled={navigation.state === 'submitting'}
              loading={navigation.state === 'submitting'}
              text="Sign in"
            />
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
}
